import './index.less'

import {m} from '@codeurs/front'
import * as Sentry from '@sentry/react'
import 'assets/icons.font'
import 'assets/images/climat-logo.svg'
import favicon_fr from 'assets/images/favicon_fr.png'
import favicon_nl from 'assets/images/favicon_nl.png'
import 'assets/images/klimaat-logo.svg'
import {en, fr, nl} from 'copy'
import 'core-js/features/array/find'
import 'core-js/features/array/find-index'
import 'core-js/features/array/from'
import 'core-js/features/string/includes'
import {createBrowserHistory} from 'history'
import React, {Component, useEffect, useMemo, useState} from 'react'
import {render} from 'react-dom'
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import {QueryParamProvider} from 'use-query-params'
import {I18n} from 'util/i18n'
import {ScrollMemory} from 'util/scrollmemory'
import {
  Contact,
  Dictionary,
  Error404,
  Home,
  Library,
  Page,
  Search,
  Sitemap,
  Tags
} from 'views/pages'
import {ClimateChange} from 'views/pages/climatechange'
import {Cookies} from 'views/pages/cookies'
import {Home2050} from 'views/pages/home2050'
import {HomeCbam} from 'views/pages/homecbam'
import {My2050Tool} from 'views/pages/my2050tool'

if (process.env.SENTRY_DSN)
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    integrations: [new Sentry.BrowserTracing()],
    environment: process.env.NODE_ENV
  })

const pages = {
  page: Page,
  parent_page: Page,
  subcategory: Page,
  contentpage: Page,
  news_item: Page,
  contact: Contact,
  error404: Error404,
  home: Home,
  category: Error404,
  my2050_contentpage: My2050Tool,
  library: Library,
  documents: Library,
  presentations: Library,
  media: Library,
  news: Library,
  links: Library,
  search: Search,
  dictionary: Dictionary,
  2050: Home2050,
  cbam: HomeCbam,
  sitemap: Sitemap,
  tags: Tags,
  climatechange_be: ClimateChange,
  cookies: Cookies
}

const languages = {
  nl,
  fr,
  en
}

const initial = window['__INITIAL_STATE__']

const history = createBrowserHistory()

const RouteAdapter = ({children}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const adaptedHistory = useMemo(
    () => ({
      replace(location) {
        navigate(location, {replace: true, state: location.state})
      },
      push(location) {
        navigate(location, {replace: false, state: location.state})
      }
    }),
    [navigate]
  )
  return children({history: adaptedHistory, location})
}

type LinkHeadType = {
  type: string
  rel: string
  href: string
}

const setFavicon = (lang: string) => {
  const link: Element & LinkHeadType =
    document.querySelector("link[rel*='icon']") ||
    document.createElement('link')
  link.type = 'image/x-icon'
  link.rel = 'shortcut icon'
  link.href = lang === 'nl' ? favicon_nl.src : favicon_fr.src
}

const PageLoader: React.FC = () => {
  const location = useLocation()
  const {pathname, search} = location
  const [error, setError] = useState<string>('')
  const [isInitialData, setIsInitialData] = useState<boolean>(true)
  const [data, setData] = useState(initial)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [dataUrl, setDataUrl] = useState<string>(
    `${pathname + search + (search ? '&' : '?')}json`
  )

  // set the favicon when the language changes
  useEffect(() => {
    setFavicon(data.language)
  }, [data.language])

  // set the dataUrl when the location changes
  useEffect(() => {
    const newDataUrl = `${pathname + search + (search ? '&' : '?')}json`
    if (dataUrl !== newDataUrl) {
      setDataUrl(newDataUrl)
    }
  }, [pathname, search])

  // fetch the data when the dataUrl changes
  useEffect(() => {
    // we do not fetch the initial page data as it is already in the initial state
    if (isInitialData) {
      setIsInitialData(false)
      return
    }

    const abortController = new AbortController()
    const {signal} = abortController

    setIsLoading(true)
    fetch(dataUrl, {signal})
      .then(res => {
        if (!res.ok) {
          throw new Error('Network response was not ok')
        }
        return res.json()
      })
      .then(setData)
      .then(() => setError(''))
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Fetch error:', error)
          setError(error.message)
        }
      })
      .finally(() => setIsLoading(false))

    return () => {
      abortController.abort() // Cancel the fetch if dataUrl changes
    }
  }, [dataUrl])

  let content: any = data
  const language = data.language
  if (!isLoading) {
    if (data.metadata || data.title) {
      document.title =
        data.metadata.title ||
        data.title ||
        (language === 'nl' ? 'Klimaat.be' : 'Climate.be')
    }
  }
  if (!isLoading && error) {
    content = error
  }
  const {key, ...entry} = content || data || (error as any)
  const View =
    pages[key] || pages[entry.channel] || (entry.own_page ? Page : Error404)
  const props = {
    data: entry,
    isLoading,
    error,
    location: location
  }
  const view = View.prototype?.render ? <View {...props} /> : View(props)

  return (
    <I18n language={language} data={languages[language]}>
      <ScrollMemory history={history} location={location} />
      {view}
    </I18n>
  )
}

class App extends Component {
  render() {
    return (
      <BrowserRouter>
        <QueryParamProvider ReactRouterRoute={RouteAdapter}>
          <Routes>
            <Route path="*" element={<PageLoader />} />
          </Routes>
        </QueryParamProvider>
      </BrowserRouter>
    )
  }
}
render(<App />, document.getElementById('root'))
